import { ReactNode, useEffect, useRef, useState } from 'react';
import { AppBar, IconButton, Toolbar, Container, Box, Button } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useTheme } from '@emotion/react';

import useScrollAction from 'hooks/useScrollAction';
import { ampli } from 'services/ampli';
import BurgerMenu from 'components/BurgerMenu';
import Logo from 'components/Logo/Logo';
import { LanguagePicker } from './LanguagePicker';
import { useTranslation } from 'hooks/useTranslation';

interface Props {
    background?: string;
    customButton?: ReactNode;
}

const Header = ({ background, customButton }: Props) => {
    const theme = useTheme();
    const [open, setOpen] = useState(false);
    const [languagePickerOpen, setLanguagePickerOpen] = useState(false);
    const [hasScrolled] = useScrollAction();
    const { i18n, detect } = useTranslation();
    const languagePickerRef = useRef<HTMLDivElement | null>(null);

    const handleClickOutsideLanguagePicker = (event: MouseEvent) => {
        const target = event.target as HTMLElement;

        if (languagePickerRef.current && !languagePickerRef.current.contains(target)) {
            setLanguagePickerOpen(false);
        }
    };

    const handleClose = () => {
        setOpen(false);
    };

    const toggleDrawer = () => {
        setOpen((open) => {
            if (ampli.isLoaded && !open) {
                ampli.landingMenuClicked();
            }

            return !open;
        });
    };

    const handleLanguagePickerClick = () => setLanguagePickerOpen(!languagePickerOpen);

    useEffect(() => {
        if (languagePickerOpen) {
            document.addEventListener('click', handleClickOutsideLanguagePicker);
        } else {
            document.removeEventListener('click', handleClickOutsideLanguagePicker);
        }

        return () => {
            document.removeEventListener('click', handleClickOutsideLanguagePicker);
        };
    }, [languagePickerOpen]);

    return (
        <AppBar
            position="fixed"
            sx={{
                // background: background || (theme as any).palette.background.default,
                background: hasScrolled ? background || (theme as any).palette.background.default : 'transparent',
                boxShadow: 'none',
                p: '13px 20px',
                minHeight: '34px',
                transition: 'background 0.3s ease-in-out',
            }}
        >
            <Container
                sx={{
                    '&.MuiContainer-root': {
                        px: { xs: '0', lg: '40px' },
                    },
                }}
            >
                <Toolbar sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', p: '0', minHeight: '34px' }}>
                    <Logo width={'32px'} height={'32px'} sx={{ cursor: 'pointer' }} />
                    <Box
                        sx={{ position: 'relative', ml: detect.isArabicLanguage ? 0 : 'auto', mr: detect.isArabicLanguage ? 'auto' : 0 }}
                        ref={languagePickerRef}
                    >
                        <Button
                            style={{
                                color: '#fff',
                                fontSize: '18px',
                                minWidth: '44px',
                                fontWeight: 300,
                            }}
                            onClick={handleLanguagePickerClick}
                        >
                            {i18n.language}
                        </Button>
                        {languagePickerOpen && <LanguagePicker onClose={() => setLanguagePickerOpen(false)} />}
                    </Box>
                    {customButton ? (
                        customButton
                    ) : (
                        <IconButton size="large" edge="end" color="inherit" aria-label="menu" onClick={toggleDrawer} sx={{ py: 0 }}>
                            <MenuIcon />
                        </IconButton>
                    )}
                </Toolbar>
            </Container>
            <BurgerMenu open={open} anchor={'right'} onClose={handleClose} />
        </AppBar>
    );
};

export default Header;
